/**
 * @description Provides a user settings context that defines things like user information (name, username, etc.), rbac access,
 * user menu navigation, and application navigation
 */

import React from 'react';
import { PropTypes } from 'prop-types';

import decodeJson from '@seccon/shared/utils/decodeJson';

// The sessionStorage key for the navigation cache
const NAVIGATION_CACHE_KEY = 'navigation-cache';

// The version number of the navigation cache
// NOTE: Any changes to the API response regarding navigation should result in this version number being incremented
const NAVIGATION_CACHE_VERSION = 'v1.0.0';

export const UserSettingsContext = React.createContext(null);

export const UserSettingsProvider = ({ children }) => {
  /**
   * Compile the userHash based on the userId stored in cookie storage and the navigation cache version number. If
   * the userId could not be retrieved, set the userHash to null.
   *
   * The hash is based on the userId and cache version number for multiple reasons.  By using the userId, we can
   * prevent the navigation being loaded based on another user's permissions. Plus, we can easily clear the cache
   * of other users by simply defining a new cache every time.  By using a cache version number, we can invalidate any
   * previously cached values if we need to make some updates to the API.
   */

  const userId = document.cookie.match(/USERID=(\d+)/i)?.[1];
  const userHash = userId ? `userId-${ userId }_${ NAVIGATION_CACHE_VERSION }` : null;

  /**
   * Retrieves the navigation cache from sessionStorage
   */
  function getNavigationCache () {
    try {
      // Read the cache from sessionStorage and parse the JSON
      const cacheRaw = sessionStorage.getItem(NAVIGATION_CACHE_KEY);
      const cache = cacheRaw ? JSON.parse(cacheRaw) : {};

      // If a userHash was defined, retrieve this user's navigation cache. Otherwise return null.
      return (userHash && cache[userHash]) ?? null;
    } catch (err) {
      // do nothing
    }

    return null;
  }

  /**
   * Sets the navigation cache in sessionStorage
   * @param {*} response - API response
   */
  function setNavigationCache (response) {
    // If no userHash is defined, do nothing
    if (!userHash) {
      return;
    }

    const cache = {};

    // Retrieve specific navigation data values from the API response and add it to the cache
    if (response) {
      (['classifiedMaterialsNavigation', 'mainNavigation', 'otherModules', 'systemAdminNavigation', 'userNavigation', 'visitsNavigation']).forEach((key) => {
        if (key in response) {
          cache[key] = response[key];
        }
      });
    }

    // Store the new cache for this user
    sessionStorage.setItem(NAVIGATION_CACHE_KEY, JSON.stringify({
      [userHash]: cache
    }));
  }

  // Manages the user settings state
  const [settings, setSettings] = React.useState(getNavigationCache);

  /**
   * Handles API success by storing the response
   * @param {*} response - API response
   */
  function handleSuccess (response) {
    setSettings(response);
    setNavigationCache(response);
  }

  /**
   * On mount, fetch the user information
   * TODO: Add error handling behavior once the toast context is accessible
   */
  React.useEffect(() => {
    fetch('/user/me')
      .then(decodeJson)
      .then(handleSuccess);
  }, []);

  return (
    <UserSettingsContext.Provider value={settings}>
      {children}
    </UserSettingsContext.Provider>
  );
};

UserSettingsProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]).isRequired
};
